import React from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import Tippy, { type TippyProps } from '@tippyjs/react';
import styles from '../styles.module.css';
import { type BaseTool } from '../tools/Tool';
import WhiteboardController from '../controllers/WhiteboardController';
import PromoPopover from '../utils/PromoPopover';
import { TOOLTIP_DELAY_GENERAL } from '../constants';
import { tippyPopoverPlugin } from '../utils/tippyPopoverPlugin';

interface WithHintProps extends Pick<TippyProps, 'singleton'> {
  name?: string;
  tool?: typeof BaseTool | null;
  delay?: TippyProps['delay'];
  placement?: TippyProps['placement'];
  appendTo?: TippyProps['appendTo'];
  children: React.ReactNode;
  zIndex?: number;
}

// Optionally display a hint
const WithHint: React.FC<WithHintProps> = ({
  name,
  tool,
  singleton,
  children,
  delay,
  placement,
  appendTo,
  zIndex = 12000,
}) => {
  const controller = useController(WhiteboardController);
  const selection = controller.toolbarObject();

  name ||= tool?.hint({ controller, selection });
  delay ||= TOOLTIP_DELAY_GENERAL;

  if (name) {
    const shortcut = tool?.shortcut?.pretty;
    const popover = tool?.popover({ controller });

    if (popover?.title) {
      return (
        <PromoPopover popover={popover}>
          <span>{children}</span>
        </PromoPopover>
      );
    }

    return (
      <Tippy
        singleton={singleton}
        delay={delay}
        placement={placement}
        appendTo={appendTo}
        content={
          <span>
            {name}{' '}
            {shortcut && (
              <span className={styles.hintToolShortcut}>{shortcut}</span>
            )}
          </span>
        }
        zIndex={zIndex}
        plugins={[tippyPopoverPlugin]}
      >
        <span>{children}</span>
      </Tippy>
    );
  }

  return <>{children}</>;
};

export default ApplicationView(WithHint);
